import React from 'react';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { Chip, ClosableMenu, ClosableMenuItem } from '@uplift/ui-v2';
import { CalendarDateService, DateObject } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { ProviderMedicalRole } from '@uplift/types';
import { makeStyles } from 'tss-react/mui';


const quickLinksByMedicalRole: Record<ProviderMedicalRole, {
  label: (today: DateObject, date: DateObject) => string;
  links: (today: DateObject) => { value: string; label: string }[];
}> = {
  [ProviderMedicalRole.therapist]: {
    label: (today, date) => {
      const days = date.diff(today, 'days');

      if (days <= 6) {
        return 'Within 1 week';
      }

      if (days >= 7 && days <= 11) {
        return '1 week out';
      }

      if (days >= 12 && days <= 17) {
        return '2 weeks out';
      }

      if (days >= 18 && days <= 24) {
        return '3 weeks out';
      }

      if (days >= 25 && days <= 28) {
        return '4 weeks out';
      }

      return 'Over 4 weeks';
    },
    links: (today) => {
      return [
        { value: today.add(1, 'week').format(DateFormats.ISO_DATE), label: '1 week out' },
        { value: today.add(2, 'week').format(DateFormats.ISO_DATE), label: '2 weeks out' },
        { value: today.add(3, 'week').format(DateFormats.ISO_DATE), label: '3 weeks out' },
        { value: today.add(4, 'week').format(DateFormats.ISO_DATE), label: '4 weeks out' },
      ];
    }
  },
  [ProviderMedicalRole.psychiatrist]: {
    label: (today, date) => {
      const days = date.diff(today, 'days');

      if (days <= 13) {
        return 'Within 2 weeks';
      }

      if (days >= 14 && days <= 21) {
        return '2 weeks';
      }

      if (days >= 22 && days <= 35) {
        return '4 weeks';
      }

      if (days >= 36 && days <= 49) {
        return '6 weeks';
      }

      if (days >= 50 && days <= 70) {
        return '8 weeks';
      }

      if (days >= 71 && days <= 84) {
        return '12 weeks';
      }

      return 'Over 12 weeks';
    },
    links: (today) => {
      return [
        { value: today.add(2, 'week').format(DateFormats.ISO_DATE), label: '2 weeks' },
        { value: today.add(4, 'week').format(DateFormats.ISO_DATE), label: '4 weeks' },
        { value: today.add(6, 'week').format(DateFormats.ISO_DATE), label: '6 weeks' },
        { value: today.add(8, 'week').format(DateFormats.ISO_DATE), label: '8 weeks' },
        { value: today.add(12, 'week').format(DateFormats.ISO_DATE), label: '12 weeks' },
      ];
    }
  },
};


const useStyles = makeStyles()((theme) => ({
  chipDeleteIcon: {
    color: theme.palette.action.active,
  },
}));


export const QuickCalendarNavigation: React.VFC<{
  medicalRole: ProviderMedicalRole;
  today: string;
  date: string;
  goToDate: (d: string) => void;
}> = ({
  medicalRole: _medicalRole,
  today: _today,
  date: _date,
  goToDate,
}) => {
  const { classes } = useStyles();
  const [ open, setOpen ] = React.useState(false);
  const anchorElRef = React.useRef<HTMLDivElement>(null);

  const today = React.useMemo(() => CalendarDateService.date(_today, DateFormats.ISO_DATE), [ _today ]);
  const date = React.useMemo(() => CalendarDateService.date(_date, DateFormats.ISO_DATE), [ _date ]);

  const helpersForMedicalRole = quickLinksByMedicalRole[_medicalRole];
  const label = React.useMemo(() => helpersForMedicalRole.label(today, date), [ today, date, helpersForMedicalRole ]);
  const links = React.useMemo(() => helpersForMedicalRole.links(today), [ today, helpersForMedicalRole ]);

  return (
    <>
      <Chip
        label={label}
        variant="outlined"
        clickable
        classes={{ deleteIcon: classes.chipDeleteIcon }}
        deleteIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          setOpen(true);
        }}
        onDelete={() => {
          setOpen(true);
        }}
        ref={anchorElRef}
      />
      <ClosableMenu
        open={open}
        anchorEl={anchorElRef.current}
        onClose={() => setOpen(false)}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {links.map(link =>
          <ClosableMenuItem key={link.value} onClick={() => goToDate(link.value)}>{link.label}</ClosableMenuItem>
        )}
      </ClosableMenu>
    </>
  );
};
